import Close16 from '@carbon/icons-react/es/close/16';
import Money20 from '@carbon/icons-react/es/money/20';
import Timer20 from '@carbon/icons-react/es/timer/20';
import { uniqBy } from 'lodash';
import React, { FC, useMemo, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { useQueryClient } from 'react-query';
import { UserType } from 'api/models';
import {
  FinancialNotificationEventType,
  ThresholdConfig,
} from 'api/settings-financials/types';
import { Button } from 'components/atoms/button';
import { Checkbox } from 'components/atoms/checkbox';
import {
  CustomLayoutDrawer,
  CustomLayoutDrawerContent,
} from 'components/atoms/drawer';
import { Form } from 'components/atoms/form';
import { InputNumber } from 'components/atoms/input';
import { Select } from 'components/atoms/select';
import { toastService } from 'components/molecules/toast';
import { useGetAllActiveUsers } from 'hooks/use-idb';
import { useTranslate } from 'hooks/use-translation';
import {
  FinancialNotificationsConfigQueryKey,
  useFinancialNotificationsConfigMutation,
  // eslint-disable-next-line import/no-internal-modules
} from 'queries/financial-notifications';
import { byLabel } from 'utils/comparators/by-label';
import { fullName } from 'utils/full-name';
import { getRandomColor } from 'utils/get-random-color';
import {
  AlertHeader,
  ButtonGroup,
  FormItem,
  Spacer,
  StyledCheckBox,
} from './styles';

// const SlackDescription = styled.div`
//   font-size: var(--font-size-sm);
//   color: var(--color-80);
// `;

export enum NotifyPeopleTypes {
  NotifyProjectOwners = 'NOTIFY_PROJECT_OWNERS',
  NotifyProjectMembers = 'NOTIFY_PROJECT_MEMBERS',
}

export interface AlertFormConfig {
  thresholdValue: number;
  notifyUsers: { value: number; label: string }[];
  notifyPeople: NotifyPeopleTypes[];
}

interface InputRef {
  focus: () => void;
}

export const SetupAlertDrawer: FC<{
  intialValues?: AlertFormConfig;
  updateThresholdId?: number;
  visible: boolean;
  onClose: () => void;
  alerts: ThresholdConfig[];
  configId: number;
  type: FinancialNotificationEventType;
}> = ({
  visible,
  onClose,
  type,
  intialValues,
  configId,
  alerts,
  updateThresholdId,
}) => {
  const translate = useTranslate();
  const [alertConfig] = Form.useForm();
  const queryClient = useQueryClient();
  const activeMembers = useGetAllActiveUsers();
  const inputRef = useRef<InputRef>(null);

  const userOptions = useMemo(
    () =>
      activeMembers.length
        ? activeMembers
            .filter((user) => user.userType === UserType.Native)
            .map((u) => ({
              value: u?.userId,
              label: fullName(u),
              imageUrl: u?.photoUrl,
              description: u?.role?.roleName,
              color: getRandomColor(u?.userId),
              roleId: u?.role?.roleId,
              userType: u?.userType,
              extra: {
                id: u?.userId,
                type: 'user',
                timeOffDetails: u?.userTimeOffDetails,
              },
            }))
            .sort(byLabel)
        : [],
    [activeMembers],
  );

  const { mutate, isLoading } = useFinancialNotificationsConfigMutation(
    configId,
    {
      onSuccess: () => {
        onClose();
        queryClient.refetchQueries(FinancialNotificationsConfigQueryKey());
        toastService({
          title: translate('settings.financials.alert.action.success.title'),
          message: translate(
            updateThresholdId
              ? 'settings.financials.alert.updation.success.message'
              : 'settings.financials.alert.creation.success.message',
          ),
        }).success();
      },
    },
  );

  const setFocus = (isOpen) => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  };

  return (
    <CustomLayoutDrawer
      destroyOnClose
      visible={visible}
      onClose={onClose}
      closeIcon={<Close16 />}
      afterVisibleChange={setFocus}
      width={360}
    >
      <CustomLayoutDrawerContent>
        <CustomLayoutDrawerContent.Title>
          <AlertHeader>
            {type ===
            FinancialNotificationEventType.ProjectBudgetThresholdReached ? (
              <Money20 />
            ) : (
              <Timer20 />
            )}
            {!updateThresholdId
              ? translate(`settings.financials.setup_alert.${type}.title`)
              : translate(`settings.financials.setup_alert.${type}.edit.title`)}
          </AlertHeader>
        </CustomLayoutDrawerContent.Title>

        <CustomLayoutDrawerContent.Content>
          <Form
            onFinish={(values) => {
              const {
                thresholdValue,
                notifyPeople,
                notifyUsers = [],
              } = values as AlertFormConfig;
              const users = ((notifyUsers as typeof userOptions) ?? [])
                .map((option) => {
                  return option.value;
                })
                .filter(Boolean);

              const payload = {
                accountNotificationConfigurationMeta: {
                  projectThresholdNotificationConditions: uniqBy(
                    [
                      {
                        thresholdValue,
                        thresholdId: updateThresholdId || Date.now(),
                        notifyProjectOwners: notifyPeople.includes(
                          NotifyPeopleTypes.NotifyProjectOwners,
                        ),
                        notifyProjectMembers: notifyPeople.includes(
                          NotifyPeopleTypes.NotifyProjectMembers,
                        ),
                        notifyUsers: users,
                      } as ThresholdConfig,
                      ...alerts,
                    ],
                    'thresholdId',
                  ),
                },
              };

              mutate(payload);
            }}
            initialValues={
              intialValues || {
                notifyPeople: [NotifyPeopleTypes.NotifyProjectOwners],
              }
            }
            form={alertConfig}
            layout="vertical"
          >
            <Form.Item
              rules={[
                {
                  required: true,
                  message: <FormattedMessage id="input.required" />,
                },
              ]}
              name="thresholdValue"
              label={translate(
                'settings.financials.setup_alert.notify_on_threshold',
              )}
            >
              <InputNumber
                ref={inputRef}
                placeholder={translate(
                  'settings.financials.alert.input.placeholder',
                )}
              />
            </Form.Item>
            <FormItem name="notifyPeople">
              <Checkbox.Group>
                <StyledCheckBox
                  value={NotifyPeopleTypes.NotifyProjectOwners}
                  disabled
                >
                  {translate(
                    'settings.financials.setup_alert.notify.project_owners',
                  )}
                </StyledCheckBox>
                <Spacer />
                <StyledCheckBox value={NotifyPeopleTypes.NotifyProjectMembers}>
                  {translate(
                    'settings.financials.setup_alert.notify.team_members',
                  )}
                </StyledCheckBox>
              </Checkbox.Group>
            </FormItem>

            <Form.Item
              name="notifyUsers"
              label={translate('settings.financials.setup_alert.notify.others')}
            >
              <Select
                isMulti
                type="user"
                closeMenuOnSelect={false}
                isClearable={false}
                options={userOptions}
              />
            </Form.Item>
            {/* <Form.Item
                label={translate(
                  'settings.financials.setup_alert.notify.slack',
                )}
              >
                <Select />
                <SlackDescription>
                  {translate(
                    'settings.financials.setup_alert.notify.slack.description',
                  )}
                </SlackDescription>
              </Form.Item> */}
          </Form>
        </CustomLayoutDrawerContent.Content>

        <CustomLayoutDrawerContent.Footer>
          <ButtonGroup>
            <Button onClick={onClose}>{translate('cancel')}</Button>
            <Button
              loading={isLoading}
              type="primary"
              onClick={alertConfig.submit}
            >
              {!updateThresholdId ? translate('create') : translate('save')}
            </Button>
          </ButtonGroup>
        </CustomLayoutDrawerContent.Footer>
      </CustomLayoutDrawerContent>
    </CustomLayoutDrawer>
  );
};
